/**
 * AK-938 — «Заложить опыт» по схеме, 4 шага (low-fi 3·А–3·Г, flow founder 5–8):
 *
 *   1. схема — из действующих схем компании;
 *   2. правка копии для этого поля (препараты, дозы, сроки; схема не меняется);
 *   3. хозяйство, поле, культура, дата закладки;
 *   4. контуры вариантов на карте поля — фигура N = вариант N (К, Э, 1, 2…).
 *
 * «Заложить опыт» — одно действие: зоны вариантов создаются по одной, затем
 * `POST …/trials/from-scheme` (идемпотентно по id). Отказ на любом шаге
 * откатывает созданные зоны — на поле не остаётся «ничьих» контуров.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { PageWithRail } from '@/components/ui/page-with-rail';
import { Stack } from '@/components/ui/stack';
import { FieldContourMiniMap } from '@/components/map/FieldContourMiniMap';
import { PickList } from '@/components/science/PickList';
import { VariantsEditor } from '@/components/science/VariantsEditor';
import { useCropPicker, useSubzones } from '@/components/fields/card/api';
import type { EditorShapes } from '@/components/fields/card/zoneSplit';
import { useFieldContourGeo } from '@/components/fields/card/widgets/widgetApi';
import { useAvailableScopes } from '@/components/scope/useAvailableScopes';
import { api, ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { scienceKeys, useTrialScheme, useTrialSchemes } from '@/lib/science/api';
import { FACTOR_LABELS, formatHa, variantMarks } from '@/lib/science/labels';
import { pickFieldContour, planVariantZones } from '@/lib/science/placement';
import {
  buildInputs,
  schemeToDraft,
  type DraftErrors,
  type VariantDraft,
} from '@/lib/science/schemeDraft';
import { useScienceSeasonLensHost } from '@/lib/science/seasonLens';
import type { FieldTrial, TrialPlacementBody } from '@/lib/science/types';

/** Скрытых слоёв нет; один экземпляр, чтобы мини-карта не перерисовывала слои на каждый рендер. */
const NO_HIDDEN: Set<string> = new Set();

export interface PlaceSearch {
  scheme?: string;
  farm?: string;
  field?: string;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v ? v : undefined;
}

export function placeSearch(search: Record<string, unknown>): PlaceSearch {
  return { scheme: str(search.scheme), farm: str(search.farm), field: str(search.field) };
}

interface FieldRow {
  id: string;
  name: string;
  area_ha?: number | null;
}

function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function newId(): string {
  return crypto.randomUUID();
}

const STEP_TITLES = [
  'выберите схему',
  'поправьте копию для этого поля (схема не меняется)',
  'хозяйство, поле и культура',
  'контуры вариантов на поле',
];

export function PlaceTrialPage({ search }: { search: PlaceSearch }) {
  useScienceSeasonLensHost();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(search.scheme ? 2 : 1);
  const [schemeId, setSchemeId] = useState<string | null>(search.scheme ?? null);
  const [variants, setVariants] = useState<VariantDraft[] | null>(null);
  const [errors, setErrors] = useState<DraftErrors>({});
  const [farmId, setFarmId] = useState<string | null>(search.farm ?? null);
  const [fieldId, setFieldId] = useState<string | null>(search.field ?? null);
  const [cropId, setCropId] = useState<string | null>(null);
  const [laidAt, setLaidAt] = useState(todayIso());
  const [shapes, setShapes] = useState<EditorShapes>([]);
  const [resetSignal, setResetSignal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const placementId = useRef(newId());

  const schemes = useTrialSchemes();
  const scheme = useTrialScheme(schemeId);
  const scopes = useAvailableScopes();
  const crops = useCropPicker();
  const fields = useQuery({
    queryKey: ['science', 'farm-fields', farmId],
    queryFn: () =>
      api.get<{ items: FieldRow[] }>(`/api/v1/fields?farm_id=${farmId}&limit=2000`),
    enabled: !!farmId,
    staleTime: 60_000,
  });
  const contours = useFieldContourGeo(fieldId ?? '');
  const subzones = useSubzones(fieldId ?? '');

  // Схема выбрана — копия вариантов для правки (шаг 2).
  useEffect(() => {
    if (scheme.data && scheme.data.id === schemeId) setVariants(schemeToDraft(scheme.data).variants);
  }, [scheme.data, schemeId]);

  const activeSchemes = (schemes.data ?? []).filter((s) => s.status === 'active');
  const farmItems = useMemo(
    () =>
      (scopes.data?.available ?? [])
        .filter((s) => s.scope_type === 'farm' && s.scope_id)
        .map((s) => ({ value: s.scope_id as string, label: s.name })),
    [scopes.data],
  );
  const fieldItems = (fields.data?.items ?? []).map((f) => ({
    value: f.id,
    label: f.name,
    hint: f.area_ha != null ? formatHa(f.area_ha) : null,
  }));
  const cropItems = (crops.data ?? [])
    .filter((c) => c.crop_id)
    .map((c) => ({ value: c.crop_id as string, label: c.name }));
  const fieldName = fieldItems.find((f) => f.value === fieldId)?.label ?? null;

  const contour = useMemo(() => pickFieldContour(contours.data), [contours.data]);
  const existing = useMemo(() => subzones.data ?? [], [subzones.data]);
  const existingZones = useMemo(
    () =>
      existing.map((z, i) => ({ id: z.id, geojson: z.geojson, kind: z.subzone_kind, index: i })),
    [existing],
  );
  const overlapMode: 'partition' | 'overlay' =
    (existing[0]?.overlap_mode as 'partition' | 'overlay' | undefined) ?? 'overlay';
  const plan = useMemo(
    () =>
      planVariantZones(
        contour,
        shapes,
        variants?.length ?? 0,
        overlapMode === 'partition' ? existing.map((z) => z.geojson) : [],
      ),
    [contour, shapes, variants, existing, overlapMode],
  );
  const marks = variantMarks((variants ?? []).map((v) => v.role));
  const drawnCount = plan.ok ? plan.zones.length : 0;
  const allDrawn = !!variants && plan.ok && drawnCount === variants.length;

  function next() {
    setSubmitError(null);
    if (step === 1) {
      if (!schemeId) return setSubmitError('Выберите схему');
      return setStep(2);
    }
    if (step === 2) {
      const errs: DraftErrors = {};
      for (const v of variants ?? []) buildInputs(v.inputs, errs);
      setErrors(errs);
      if (Object.keys(errs).length) return;
      return setStep(3);
    }
    if (step === 3) {
      if (!farmId || !fieldId) return setSubmitError('Выберите хозяйство и поле');
      if (laidAt > todayIso()) return setSubmitError('Дата закладки — не позже сегодняшней');
      return setStep(4);
    }
  }

  async function place() {
    if (!fieldId || !schemeId || !variants || !plan.ok || !allDrawn || busy) return;
    setBusy(true);
    setSubmitError(null);
    const created: string[] = [];
    try {
      for (let i = 0; i < variants.length; i += 1) {
        const zone = await api.post<{ id: string }>(`/api/v1/fields/${fieldId}/subzones`, {
          subzone_kind: 'trial',
          overlap_mode: overlapMode,
          name: variants[i].name,
          number: marks[i],
          geojson: plan.zones[i].geojson,
          valid_from: laidAt,
        });
        created.push(zone.id);
      }
      const errs: DraftErrors = {};
      const body: TrialPlacementBody = {
        id: placementId.current,
        scheme_id: schemeId,
        name: fieldName && scheme.data ? `${fieldName} · ${scheme.data.name}` : null,
        laid_at: laidAt,
        status: 'laid',
        crop_id: cropId,
        variants: variants.map((v, i) => ({
          scheme_variant_id: v.schemeVariantId as string,
          subzone_id: created[i],
          treatment_note: v.note.trim() || null,
          inputs: buildInputs(v.inputs, errs),
        })),
      };
      const trial = await api.post<FieldTrial>(
        `/api/v1/fields/${fieldId}/trials/from-scheme`,
        body,
      );
      toast.success('Опыт заложен');
      void navigate({
        to: '/science/trials/$fieldId/$trialId',
        params: { fieldId, trialId: trial.id },
      });
    } catch (e) {
      const detail = e instanceof ApiError ? e.detail : 'неизвестная ошибка';
      let rolledBack = true;
      for (const id of [...created].reverse()) {
        try {
          await api.delete(`/api/v1/fields/${fieldId}/subzones/${id}`);
        } catch {
          rolledBack = false;
        }
      }
      setSubmitError(
        rolledBack
          ? `Опыт не заложен: ${detail}. Контуры не сохранены — можно повторить.`
          : `Опыт не заложен: ${detail}. Часть контуров осталась на поле — удалите их во вкладке «Геометрия».`,
      );
    } finally {
      await qc.invalidateQueries({ queryKey: scienceKeys.all });
      void qc.invalidateQueries({ queryKey: ['field-card', 'subzones', fieldId] });
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <Stack gap={6}>
        <PageHeader
          title="Заложить опыт"
          description={`Шаг ${step} из 4 · ${STEP_TITLES[step - 1]}${step === 4 && fieldName ? ` «${fieldName}»` : ''}`}
          breadcrumbs={[{ label: 'Наука', to: '/science' }, { label: 'Заложить опыт' }]}
          actions={
            <div className="flex flex-wrap gap-2">
              {step > 1 && (
                <Button variant="outline" disabled={busy} onClick={() => setStep(step - 1)}>
                  <ArrowLeft className="h-4 w-4" />
                  Назад
                </Button>
              )}
              {step < 4 ? (
                <Button onClick={next} disabled={step === 2 && !variants}>
                  Дальше
                </Button>
              ) : (
                <Button onClick={() => void place()} disabled={!allDrawn || busy}>
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Заложить опыт
                </Button>
              )}
            </div>
          }
        />

        {submitError && (
          <Alert variant="destructive">
            <AlertDescription role="alert">{submitError}</AlertDescription>
          </Alert>
        )}

        {step === 1 && (
          <Card>
            <CardHeader className="p-4 pb-3">
              <CardTitle className="text-base">Схемы компании</CardTitle>
              <p className="text-sm text-muted-foreground">
                опыт закладывается по действующей схеме; черновики сначала сохраните
              </p>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {schemes.isLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : activeSchemes.length === 0 ? (
                <div className="flex flex-col items-start gap-3 text-sm text-muted-foreground">
                  Действующих схем нет.
                  <Button variant="outline" onClick={() => void navigate({ to: '/science/schemes/new' })}>
                    Создать схему
                  </Button>
                </div>
              ) : (
                <RadioGroup value={schemeId ?? ''} onValueChange={setSchemeId} className="gap-0">
                  {activeSchemes.map((s) => (
                    <Label
                      key={s.id}
                      htmlFor={`scheme-${s.id}`}
                      className="flex min-h-10 cursor-pointer items-center gap-3 border-b border-border py-2 font-normal last:border-0"
                    >
                      <RadioGroupItem id={`scheme-${s.id}`} value={s.id} />
                      <span className="min-w-0 flex-1 truncate font-medium">{s.name}</span>
                      <span className="hidden gap-1 md:flex">
                        {s.factors.map((f) => (
                          <Badge key={f} variant="secondary">
                            {FACTOR_LABELS[f]}
                          </Badge>
                        ))}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {s.season_year} · вариантов {s.variants_count}
                      </span>
                    </Label>
                  ))}
                </RadioGroup>
              )}
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <CardHeader className="p-4 pb-3">
              <CardTitle className="text-base">Варианты · {scheme.data?.name ?? '…'}</CardTitle>
              <p className="text-sm text-muted-foreground">
                правки остаются в этом опыте; схема и другие опыты по ней не меняются
              </p>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {!variants ? (
                <Skeleton className="h-40 w-full" />
              ) : (
                <VariantsEditor
                  variants={variants}
                  onChange={(v) => {
                    setVariants(v);
                    setErrors({});
                  }}
                  errors={errors}
                  lockStructure
                />
              )}
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card>
            <CardHeader className="p-4 pb-3">
              <CardTitle className="text-base">Где закладываем</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 p-4 pt-0 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label>Хозяйство</Label>
                <PickList
                  aria-label="Хозяйство"
                  value={farmId}
                  items={farmItems}
                  loading={scopes.isLoading}
                  onChange={(v) => {
                    if (v !== farmId) {
                      setFarmId(v);
                      setFieldId(null);
                      setShapes([]);
                    }
                  }}
                  placeholder="Выберите хозяйство"
                  searchPlaceholder="Название хозяйства"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label>Поле</Label>
                <PickList
                  aria-label="Поле"
                  value={fieldId}
                  items={fieldItems}
                  loading={fields.isFetching}
                  disabled={!farmId}
                  onChange={(v) => {
                    setFieldId(v);
                    setShapes([]);
                    setResetSignal((n) => n + 1);
                  }}
                  placeholder={farmId ? 'Выберите поле' : 'Сначала хозяйство'}
                  searchPlaceholder="Название поля"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label>Культура</Label>
                <PickList
                  aria-label="Культура"
                  value={cropId}
                  items={cropItems}
                  loading={crops.isLoading}
                  onChange={(v) => setCropId(v)}
                  placeholder="Культура опыта (необязательно)"
                  searchPlaceholder="Название культуры"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="laid-at">Дата закладки</Label>
                <Input
                  id="laid-at"
                  type="date"
                  value={laidAt}
                  max={todayIso()}
                  onChange={(e) => setLaidAt(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {step === 4 && fieldId && (
          <PageWithRail
            rail={
            <Card>
              <CardHeader className="p-4 pb-3">
                <CardTitle className="text-base">Варианты</CardTitle>
                <p className="text-sm text-muted-foreground">
                  нарисовано {drawnCount} из {variants?.length ?? 0}
                </p>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 p-4 pt-0">
                {(variants ?? []).map((v, i) => {
                  const z = plan.ok ? plan.zones[i] : undefined;
                  return (
                    <div
                      key={v.key}
                      className="flex min-h-10 items-center justify-between gap-2 border-b border-border py-2 text-sm last:border-0"
                    >
                      <span>
                        {marks[i]} · {v.name}
                      </span>
                      <span className={z ? '' : 'text-muted-foreground'}>
                        {z ? formatHa(z.areaHa) : i === drawnCount ? 'рисуйте следующим' : 'не нарисован'}
                      </span>
                    </div>
                  );
                })}
                {!plan.ok && (
                  <p role="alert" className="text-sm text-destructive">
                    {plan.error}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Рисуйте контуры по порядку вариантов: первая фигура — {marks[0] ?? 'К'}, вторая —{' '}
                  {marks[1] ?? '…'} и так далее. Контуры — внутри поля и не пересекаются; остаток поля
                  в опыт не входит. «Заложить опыт» станет активной, когда нарисованы все варианты.
                </p>
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!shapes.length || busy}
                    onClick={() => {
                      setShapes([]);
                      setResetSignal((n) => n + 1);
                    }}
                  >
                    Нарисовать заново
                  </Button>
                </div>
              </CardContent>
            </Card>
            }
          >
            <div className="relative aspect-[4/3] min-h-[24rem] w-full overflow-hidden rounded-xl border border-border bg-muted">
              {contours.isLoading ? (
                <Skeleton className="absolute inset-0 h-full w-full" />
              ) : !contour ? (
                <div className="p-4 text-sm text-muted-foreground">
                  У поля нет контура — сначала обведите поле на карте.
                </div>
              ) : (
                <FieldContourMiniMap
                  fieldId={fieldId}
                  farmId={farmId}
                  referenceGeo={contour}
                  zones={existingZones}
                  hidden={NO_HIDDEN}
                  referenceHidden={false}
                  editing={false}
                  onEditingChange={() => {}}
                  onSaveGeometry={async () => {}}
                  zonesEditing
                  onZoneShapesChange={setShapes}
                  zonesResetSignal={resetSignal}
                />
              )}
            </div>
          </PageWithRail>
        )}
      </Stack>
    </AppShell>
  );
}
